import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Download, ChevronDown, ChevronRight, Archive as ArchiveIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ArchivedAppraisals = () => {
  const { user } = useAuth();
  const [archivedAppraisals, setArchivedAppraisals] = useState([]);
  const [years, setYears] = useState([]);
  const [expandedYears, setExpandedYears] = useState({});
  const [loading, setLoading] = useState(true);
  const [downloadingSummary, setDownloadingSummary] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appraisalsRes, yearsRes] = await Promise.all([
        axios.get(`${API_URL}/appraisals/archived`),
        axios.get(`${API_URL}/appraisals/archived/years`)
      ]);
      
      setArchivedAppraisals(appraisalsRes.data);
      setYears(yearsRes.data.years || []);
      
      // Expand the most recent year by default
      if (yearsRes.data.years && yearsRes.data.years.length > 0) {
        setExpandedYears({ [yearsRes.data.years[0]]: true });
      }
    } catch (error) {
      toast.error('Failed to load archived appraisals');
    } finally {
      setLoading(false);
    }
  };

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const handleDownloadPDF = async (appraisal) => {
    try {
      const response = await axios.get(
        `${API_URL}/appraisals/${appraisal.id}/pdf`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Appraisal_${appraisal.employee_name.replace(/\s/g, '_')}_${appraisal.cycle_name.replace(/\s/g, '_')}_${appraisal.cycle_year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleDownloadSummaryPDF = async (year) => {
    setDownloadingSummary(true);
    try {
      const response = await axios.get(
        `${API_URL}/appraisals/summary-pdf/${year}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Appraisal_Summary_${year.replace('-', '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Summary PDF for ${year} downloaded successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download summary PDF');
    } finally {
      setDownloadingSummary(false);
    }
  };

  const getCycleTypeBadge = (cycleType) => {
    const colors = {
      'Annual': 'bg-blue-100 text-blue-800 border-blue-200',
      'Half-Yearly': 'bg-teal-100 text-teal-800 border-teal-200',
      'Q1': 'bg-green-100 text-green-800 border-green-200',
      'Q2': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Q3': 'bg-orange-100 text-orange-800 border-orange-200',
      'Q4': 'bg-purple-100 text-purple-800 border-purple-200'
    };

    return (
      <Badge className={`text-xs font-semibold border ${colors[cycleType] || colors['Annual']}`}>
        {cycleType || 'Annual'}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const labels = {
      'DRAFT_SELF': 'Draft',
      'SUBMITTED_SELF': 'Pending Manager',
      'IN_MANAGER_REVIEW': 'Manager Review',
      'SECOND_LEVEL_REVIEW': 'Calibration',
      'FINALIZED': 'Finalized'
    };

    return (
      <Badge variant="outline" className="uppercase tracking-wider text-xs border border-slate-200">
        {labels[status]}
      </Badge>
    );
  };

  const getRatingDisplay = (rating) => {
    if (!rating) return <span className="text-slate-400">-</span>;
    return <span className="font-semibold text-slate-900">{rating}</span>;
  };

  const getAppraisalsByYear = (year) => {
    return archivedAppraisals.filter(a => a.cycle_year === year);
  };

  if (loading) return <div className="text-sm text-slate-700">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl tracking-tight font-semibold text-slate-900">
            <ArchiveIcon className="inline-block mr-3 mb-1" size={36} />
            Archived Appraisals
          </h1>
          <p className="text-sm text-slate-700 mt-2">
            {user.role === 'HR_ADMIN' 
              ? 'View all archived appraisals organized by year'
              : user.role === 'MANAGER'
              ? 'View your archived appraisals and those of your team members'
              : 'View your past performance appraisals'
            }
          </p>
        </div>
      </div>

      {years.length === 0 ? (
        <Card className="border border-slate-200 bg-white rounded-sm p-12 text-center">
          <ArchiveIcon className="mx-auto mb-4 text-slate-400" size={48} />
          <p className="text-slate-500 text-lg">No archived appraisals found</p>
          <p className="text-slate-400 text-sm mt-2">Completed appraisals will appear here after archiving</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {years.map(year => {
            const yearAppraisals = getAppraisalsByYear(year);
            const isExpanded = expandedYears[year];

            return (
              <Card key={year} className="border border-slate-200 bg-white rounded-sm overflow-hidden">
                <div className="w-full px-6 py-4 flex items-center justify-between bg-slate-50">
                  <button
                    onClick={() => toggleYear(year)}
                    className="flex items-center gap-3 hover:opacity-70 transition-opacity"
                  >
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <span className="text-lg font-semibold text-slate-900">{year}</span>
                    <Badge className="bg-slate-200 text-slate-700 border-slate-300">
                      {yearAppraisals.length} {yearAppraisals.length === 1 ? 'Appraisal' : 'Appraisals'}
                    </Badge>
                  </button>
                  
                  {user.role === 'HR_ADMIN' && (
                    <Button
                      onClick={() => handleDownloadSummaryPDF(year)}
                      disabled={downloadingSummary}
                      variant="outline"
                      size="sm"
                      className="border-blue-200 text-blue-700 hover:bg-blue-50 rounded-sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {downloadingSummary ? 'Generating...' : 'Summary PDF'}
                    </Button>
                  )}
                </div>

                {isExpanded && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Type</th>
                          <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Cycle</th>
                          {user.role !== 'EMPLOYEE' && (
                            <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Employee</th>
                          )}
                          <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Status</th>
                          <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Final Rating</th>
                          <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Archived</th>
                          <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {yearAppraisals.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center p-8 text-slate-500">No appraisals for this year</td>
                          </tr>
                        ) : (
                          yearAppraisals.map(appraisal => (
                            <tr key={appraisal.id} className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                              <td className="p-4">{getCycleTypeBadge(appraisal.cycle_type)}</td>
                              <td className="p-4 text-slate-700">{appraisal.cycle_name}</td>
                              {user.role !== 'EMPLOYEE' && (
                                <td className="p-4 text-slate-700 font-medium">{appraisal.employee_name}</td>
                              )}
                              <td className="p-4">{getStatusBadge(appraisal.status)}</td>
                              <td className="p-4">{getRatingDisplay(appraisal.second_level_overall_rating || appraisal.manager_overall_rating || appraisal.self_overall_rating)}</td>
                              <td className="p-4 text-slate-500">{appraisal.archived_at ? formatDistanceToNow(new Date(appraisal.archived_at), { addSuffix: true }) : '-'}</td>
                              <td className="p-4">
                                <Button
                                  onClick={() => handleDownloadPDF(appraisal)}
                                  variant="outline"
                                  size="sm"
                                  className="border-slate-200 rounded-sm"
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  PDF
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ArchivedAppraisals;
